import { Link, NavLink, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, ScanLine, Wrench, Star, Crown, LogIn, LogOut, Shield } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { ReviewsMarquee } from '@/components/ReviewsMarquee';
import { AiMechanicWidget } from '@/components/AiMechanicWidget';

const nav = [
    { to: '/shop', label: 'Shop', testid: 'header-nav-shop-link', icon: ShoppingCart },
    { to: '/vin', label: 'VIN Lookup', testid: 'header-nav-vin-link', icon: ScanLine },
    { to: '/ai-mechanic', label: 'AI Mechanic', testid: 'header-nav-ai-mechanic-link', icon: Wrench },
    { to: '/reviews', label: 'Reviews', testid: 'header-nav-reviews-link', icon: Star },
    { to: '/join', label: 'Join Club', testid: 'header-nav-join-link', icon: Crown },
];

export function Layout({ children }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const { user, logout } = useAuth();
    const { totalCount } = useCart();
    const loc = useLocation();
    const isAdminRoute = loc.pathname.startsWith('/admin');

    return (
        <div className="min-h-screen flex flex-col">
            <header className="sticky top-0 z-40 bg-black/70 backdrop-blur border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
                    <Link to="/" data-testid="header-nav-home-link" className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md bg-[var(--rrm-red)] inline-flex items-center justify-center">
                            <span className="font-display text-white text-lg leading-none">R</span>
                        </div>
                        <div>
                            <div className="font-display text-xl text-white leading-none">REAL RUDE</div>
                            <div className="text-[10px] tracking-[0.2em] text-amber-400/80 leading-none">PERFORMANCE</div>
                        </div>
                    </Link>
                    <nav className="hidden lg:flex items-center gap-1">
                        {nav.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={n.testid}
                                className={({ isActive }) =>
                                    `px-3 py-2 text-sm rounded-md inline-flex items-center gap-1.5 ${
                                        isActive ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white hover:bg-white/5'
                                    }`
                                }
                            >
                                <n.icon className="w-4 h-4" /> {n.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        <Link
                            to="/cart"
                            data-testid="header-cart-button"
                            className="relative px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 inline-flex items-center gap-1.5 text-sm"
                        >
                            <ShoppingCart className="w-4 h-4" />
                            <span className="hidden md:inline">Cart</span>
                            {totalCount > 0 && (
                                <span className="ml-1 text-[10px] bg-[var(--rrm-red)] text-white rounded-full px-1.5 py-0.5">
                                    {totalCount}
                                </span>
                            )}
                        </Link>
                        {user ? (
                            <>
                                {user.is_admin && (
                                    <Link
                                        to="/admin"
                                        data-testid="header-admin-link"
                                        className="hidden md:inline-flex px-3 py-2 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-200 text-sm items-center gap-1.5"
                                    >
                                        <Shield className="w-4 h-4" /> Admin
                                    </Link>
                                )}
                                <Link
                                    to="/account"
                                    className="hidden md:inline-block text-sm text-white/70 hover:text-white px-2"
                                    data-testid="header-account-link"
                                >
                                    {user.username}
                                </Link>
                                <button
                                    data-testid="header-logout-button"
                                    onClick={logout}
                                    className="p-2 rounded-md hover:bg-white/5"
                                    title="Sign out"
                                >
                                    <LogOut className="w-4 h-4" />
                                </button>
                            </>
                        ) : (
                            <Link
                                to="/login"
                                data-testid="header-login-button"
                                className="px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 inline-flex items-center gap-1.5 text-sm"
                            >
                                <LogIn className="w-4 h-4" />
                                <span className="hidden md:inline">Sign in</span>
                            </Link>
                        )}
                        <button
                            className="lg:hidden p-2 rounded-md hover:bg-white/5"
                            onClick={() => setMobileOpen((v) => !v)}
                            data-testid="header-mobile-toggle"
                            aria-label="toggle menu"
                        >
                            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
                {mobileOpen && (
                    <div className="lg:hidden border-t border-white/10 bg-black/90">
                        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
                            {nav.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    onClick={() => setMobileOpen(false)}
                                    className={({ isActive }) =>
                                        `px-3 py-2 rounded-md text-sm inline-flex items-center gap-2 ${
                                            isActive ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/5'
                                        }`
                                    }
                                >
                                    <n.icon className="w-4 h-4" /> {n.label}
                                </NavLink>
                            ))}
                            {user && user.is_admin && (
                                <NavLink to="/admin" onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-md text-sm bg-amber-500/15 text-amber-200">Admin Dashboard</NavLink>
                            )}
                        </div>
                    </div>
                )}
            </header>

            <main className="flex-1 pb-20 md:pb-24">{children}</main>

            {!isAdminRoute && (
                <footer className="border-t border-white/10 bg-black/60 mt-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-4 gap-8 text-sm text-white/60">
                        <div>
                            <div className="font-display text-2xl text-white">REAL RUDE</div>
                            <div className="text-xs tracking-[0.2em] text-amber-400/80">PERFORMANCE</div>
                            <p className="mt-3">Curated OEM & VIN-verified upgrades for Dodge, Mustang, and Audi. No garbage parts. Period.</p>
                        </div>
                        <div>
                            <div className="text-white font-semibold mb-3">Shop</div>
                            <ul className="space-y-1">
                                <li><Link to="/shop" className="hover:text-white">All Parts</Link></li>
                                <li><Link to="/vin" className="hover:text-white">VIN Lookup</Link></li>
                                <li><Link to="/ai-mechanic" className="hover:text-white">AI Mechanic</Link></li>
                            </ul>
                        </div>
                        <div>
                            <div className="text-white font-semibold mb-3">Club</div>
                            <ul className="space-y-1">
                                <li><Link to="/join" className="hover:text-white">Join RRM</Link></li>
                                <li><Link to="/reviews" className="hover:text-white">Reviews</Link></li>
                                <li><Link to="/login" className="hover:text-white">Sign in</Link></li>
                            </ul>
                        </div>
                        <div>
                            <div className="text-white font-semibold mb-3">Newsletter</div>
                            <p className="text-xs">Coupons, drops & build features land here first.</p>
                            <NewsletterMini />
                        </div>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 text-xs text-white/40 flex flex-wrap items-center justify-between gap-2">
                        <div>© {new Date().getFullYear()} Real Rude Performance. All rights reserved.</div>
                        <div className="text-white/40">Apple Pay • Cash App • Cards via Stripe</div>
                    </div>
                </footer>
            )}
            <ReviewsMarquee />
            {!isAdminRoute && <AiMechanicWidget />}
        </div>
    );
}

import { useState as useS2 } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
function NewsletterMini() {
    const [email, setEmail] = useS2('');
    const [busy, setBusy] = useS2(false);
    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setBusy(true);
        try {
            await api.post('/newsletter/subscribe', { email });
            toast.success('Subscribed to newsletter');
            setEmail('');
        } catch {
            toast.error('Could not subscribe');
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={subscribe} className="mt-2 flex gap-2">
            <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                type="email"
                required
                data-testid="newsletter-email-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-xs"
            />
            <button
                disabled={busy}
                data-testid="newsletter-subscribe-button"
                className="text-xs px-2.5 py-1.5 rounded-md bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30"
            >
                Subscribe
            </button>
        </form>
    );
}
